import { useState } from "react";
import { LAUNCH_PLAN_CATALOG, type PlanKey } from "@mph/launch-control";
import { posthog } from "../../lib/posthog";
import { DoorwayBrandIcon, type DoorwayBrandIconName } from "../public/DoorwayBrandIcon";

type BillingDisplayCadence = "monthly" | "annual";

type Plan = {
  name: string;
  pricing: Record<BillingDisplayCadence, {
    price: string;
    cadence: string;
    note: string;
  }>;
  description: string;
  usage: PlanFeature[];
  features: PlanFeature[];
  tone: PlanKey;
  featured?: boolean;
};

type FeatureIconName = "site" | "storage" | "visits" | "domain" | "publish" | "badge" | "password" | "ai" | "users" | "viewers" | "signin" | "controls";

type PlanFeature = {
  label: string;
  icon: FeatureIconName;
};

const catalog = LAUNCH_PLAN_CATALOG.plans;

const planDoorIcons: Record<PlanKey, DoorwayBrandIconName> = {
  free: "door-outline-forest",
  solo: "door-outline-white",
  team: "door-outline-sky",
};

const plans: Plan[] = [
  {
    name: catalog.free.name,
    pricing: {
      monthly: { price: formatPrice(catalog.free.prices.monthlyCents), cadence: "forever", note: "No card required" },
      annual: { price: formatPrice(catalog.free.prices.annualCents), cadence: "forever", note: "No card required" },
    },
    description: "Put one useful app online and share it with a Doorway link.",
    usage: [
      { label: `${catalog.free.entitlements.liveApps} live site`, icon: "site" },
      { label: `${megabytes(catalog.free.entitlements.maxAppSizeBytes)} MB per site`, icon: "storage" },
      { label: `${formatNumber(catalog.free.entitlements.monthlyAppVisits)} visits / month`, icon: "visits" },
    ],
    features: [
      { label: `${catalog.free.entitlements.doorwaySubdomains} Doorway subdomain`, icon: "domain" },
      { label: "10 publishes / month", icon: "publish" },
      { label: "Doorway badge", icon: "badge" },
    ],
    tone: "free",
  },
  {
    name: catalog.solo.name,
    pricing: {
      monthly: { price: formatPrice(catalog.solo.prices.monthlyCents), cadence: "per month", note: `${formatPrice(catalog.solo.prices.annualCents)} when paid yearly` },
      annual: { price: formatPrice(catalog.solo.prices.annualCents), cadence: "per year", note: `${monthlyEquivalent(catalog.solo.prices.annualCents)}/month, billed yearly` },
    },
    description: "Give your main app its own address, then add more branded sites as you need them.",
    usage: [
      { label: `${catalog.solo.entitlements.liveApps} live sites`, icon: "site" },
      { label: `${megabytes(catalog.solo.entitlements.maxAppSizeBytes)} MB per site`, icon: "storage" },
      { label: `${formatNumber(catalog.solo.entitlements.monthlyAppVisits)} visits / month`, icon: "visits" },
    ],
    features: [
      { label: `${catalog.solo.entitlements.customDomains} custom domain`, icon: "domain" },
      { label: `${formatPrice(catalog.solo.prices.monthlyCents)}/mo per additional site + domain`, icon: "site" },
      { label: "Password protection", icon: "password" },
      { label: "Removable Doorway badge", icon: "badge" },
      { label: "Bring your own AI key and model", icon: "ai" },
    ],
    tone: "solo",
    featured: true,
  },
  {
    name: catalog.team.name,
    pricing: {
      monthly: { price: formatPrice(catalog.team.prices.monthlyCents), cadence: "per month", note: `${formatPrice(catalog.team.prices.annualCents)} when paid yearly` },
      annual: { price: formatPrice(catalog.team.prices.annualCents), cadence: "per year", note: `${monthlyEquivalent(catalog.team.prices.annualCents)}/month, billed yearly` },
    },
    description: "Give useful internal tools one private home your company controls.",
    usage: [
      { label: `${catalog.team.entitlements.liveApps} live sites`, icon: "site" },
      { label: `${megabytes(catalog.team.entitlements.maxAppSizeBytes)} MB per site`, icon: "storage" },
      { label: `${formatNumber(catalog.team.entitlements.monthlyAppVisits)} visits / month`, icon: "visits" },
    ],
    features: [
      { label: `${catalog.team.entitlements.customDomains} custom domains`, icon: "domain" },
      { label: `Up to ${catalog.team.entitlements.creatorSeats} users`, icon: "users" },
      { label: "Unlimited approved viewers", icon: "viewers" },
      { label: "Google Workspace sign-in", icon: "signin" },
      { label: "Roles, offboarding, and audit history", icon: "controls" },
      { label: "Workspace-owned AI keys and models", icon: "ai" },
    ],
    tone: "team",
  },
];

function formatPrice(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

function monthlyEquivalent(annualCents: number): string {
  return `$${(annualCents / 1_200).toFixed(2)}`;
}

function megabytes(bytes: number): number {
  return bytes / (1024 * 1024);
}

function formatNumber(value: number): string {
  return value.toLocaleString("en-US");
}

export function PricingSection({
  variant = "default",
  eyebrow,
  heading,
}: {
  variant?: "default" | "oasis";
  eyebrow?: string;
  heading?: string;
} = {}) {
  const [billingCadence, setBillingCadence] = useState<BillingDisplayCadence>("monthly");
  return (
    <section className={`pricing-section${variant === "oasis" ? " pricing-section-oasis" : ""}`} id="plans" aria-labelledby="plans-heading">
      <div className="pricing-heading">
        <div>
          <p className="eyebrow">{eyebrow ?? (variant === "oasis" ? "Pricing" : "Simple plans")}</p>
          <h2 id="plans-heading">{heading ?? (variant === "oasis" ? "Start free, grow with your team." : "Pick the door that fits.")}</h2>
        </div>
        <div className="pricing-heading-copy">
          <p>Start free with a Doorway link. Choose Solo when your app needs its own domain. Bring the whole team when your tools become shared infrastructure.</p>
          <div className="pricing-cadence-row">
            <span>Billing</span>
            <div className="pricing-cadence-toggle" role="group" aria-label="Billing cadence">
              <button type="button" aria-pressed={billingCadence === "monthly"} onClick={() => setBillingCadence("monthly")}>Monthly</button>
              <button type="button" aria-pressed={billingCadence === "annual"} onClick={() => setBillingCadence("annual")}>Annual</button>
            </div>
            <small>Save about 2 months</small>
          </div>
        </div>
      </div>

      <div className="plan-grid">
        {plans.map((plan) => {
          const price = plan.pricing[billingCadence];
          return (
            <article
              aria-label={plan.name}
              className={`plan-card plan-${plan.tone}${plan.featured ? " plan-featured" : ""}`}
              key={plan.name}
            >
              <div className="plan-card-topline">
                <DoorwayBrandIcon className="plan-door-icon" name={planDoorIcons[plan.tone]} />
              </div>
              <h3>{plan.name}</h3>
              <div className="plan-price">
                <strong>{price.price}</strong>
                <span>{price.cadence}</span>
              </div>
              <p className="plan-annual">{price.note}</p>
              <p className="plan-description">{plan.description}</p>
              <div className="plan-feature-groups">
                <FeatureGroup title="Usage included" features={plan.usage} />
                <FeatureGroup title="Plan features" features={plan.features} />
              </div>
              <a
                className="plan-action"
                href={plan.tone === "free" ? "/manage" : `/manage/workspaces?plan=${plan.tone}&cadence=${billingCadence}`}
                onClick={() => posthog.capture(plan.tone === "free" ? "start_free_clicked" : "plan_upgrade_started", { plan: plan.tone, cadence: billingCadence })}
              >
                {plan.tone === "free" ? "Start for free" : `Choose ${plan.name}`}
              </a>
            </article>
          );
        })}
      </div>

      <aside className="domain-note" aria-label="Custom domain policy">
        <svg className="domain-icon" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8.5" />
          <path d="M3.8 12h16.4M12 3.5c2.2 2.3 3.3 5.1 3.3 8.5S14.2 18.2 12 20.5M12 3.5C9.8 5.8 8.7 8.6 8.7 12s1.1 6.2 3.3 8.5" />
        </svg>
        <div>
          <strong>Want your app at its own address?</strong>
          <span>Custom domains start with Solo. You add the DNS record Doorway gives you, then Doorway verifies it and handles SSL.</span>
        </div>
      </aside>

      <p className="pricing-disclaimer">
        AI provider charges go directly to your provider account. Doorway keeps your key out of published files and does not meter or resell AI tokens.
      </p>
    </section>
  );
}

function FeatureGroup({ title, features }: { title: string; features: PlanFeature[] }) {
  return (
    <section className="plan-feature-group" aria-label={title}>
      <h4>{title}</h4>
      <ul>
        {features.map((feature) => (
          <li key={feature.label}>
            <FeatureIcon name={feature.icon} />
            <span>{feature.label}</span>
            <i aria-hidden="true">✓</i>
          </li>
        ))}
      </ul>
    </section>
  );
}

function FeatureIcon({ name }: { name: FeatureIconName }) {
  const paths: Record<FeatureIconName, React.ReactNode> = {
    site: <><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></>,
    storage: <><path d="M7 18a4 4 0 0 1-.8-7.9A6 6 0 0 1 17.7 8 4.5 4.5 0 0 1 18 17H7Z" /><path d="m9 12 3-3 3 3M12 9v6" /></>,
    visits: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 16v2M12 12v6M16 8v10" /></>,
    domain: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>,
    publish: <><path d="M12 16V4M8 8l4-4 4 4" /><path d="M5 14v5h14v-5" /></>,
    badge: <><path d="M7 3h10v8a5 5 0 0 1-10 0V3Z" /><path d="m9 20 3-4 3 4" /></>,
    password: <><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" /></>,
    ai: <><path d="M12 3v4M12 17v4M3 12h4M17 12h4" /><circle cx="12" cy="12" r="5" /><path d="m8.5 8.5-2-2M15.5 15.5l2 2M15.5 8.5l2-2M8.5 15.5l-2 2" /></>,
    users: <><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20a6 6 0 0 1 12 0M14 15a5 5 0 0 1 7 4.5" /></>,
    viewers: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6S2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.5" /></>,
    signin: <><path d="M10 4H5v16h5M14 8l4 4-4 4M8 12h10" /></>,
    controls: <><path d="M12 3 5 6v5c0 4.8 2.9 8.1 7 10 4.1-1.9 7-5.2 7-10V6l-7-3Z" /><path d="m9 12 2 2 4-4" /></>,
  };
  return (
    <svg className="plan-feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}
